import { DragEvent, KeyboardEvent, useRef, useState } from 'react';

/** Reorder visible columns while preserving their identifiers and any saved sizes. */
export default function useColumnOrder(initialIds: Array<string>) {
  const [order, setOrder] = useState(initialIds);
  const dragging = useRef<string | null>(null);

  function move(id: string, target: string) {
    setOrder((current) => {
      const from = current.indexOf(id);
      const to = current.indexOf(target);
      if (from < 0 || to < 0 || from === to) return current;
      const next = [...current];
      next.splice(from, 1);
      next.splice(to, 0, id);
      return next;
    });
  }

  function props(id: string) {
    return {
      draggable: true,
      tabIndex: 0,
      title: 'Drag to move column, or use Alt+Shift+Left/Right',
      onDragStart: (event: DragEvent<HTMLElement>) => {
        dragging.current = id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', id);
      },
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (dragging.current) event.preventDefault();
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        if (!dragging.current) return;
        event.preventDefault();
        move(dragging.current, id);
        dragging.current = null;
      },
      onDragEnd: () => {
        dragging.current = null;
      },
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (event.target !== event.currentTarget || !event.altKey || !event.shiftKey) return;
        const delta = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
        const target = order[order.indexOf(id) + delta];
        if (delta && target) {
          event.preventDefault();
          move(id, target);
        }
      },
    };
  }
  return { order, columnDragProps: props };
}
